import { useMemoizedFn, useUpdateEffect } from 'ahooks';
import { Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { UnderlineText } from 'components/RevxTypography';
import {
  SKU_SERVICE_GENRE_ENUM,
  SKU_SERVICE_TYPE_ENUM,
  SvcGroup,
  SvcShowTypeEnum,
} from 'domains/skuDomain';
import { cloneDeep } from 'lodash-es';
import { useEffect, useImperativeHandle, useState } from 'react';
import { StepComponentSvcProps } from '../SingleWizardStep';
import './JobOptionConfig.less';
import { SvcTree, SvcTreeValue } from './SvcTree';

export const JobOptionConfig = (props: StepComponentSvcProps) => {
  const { sku, showType, onRef, disabled, refSubmitData } = props;
  // const { t } = useTranslation();
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const [svcListLength, setSvcListLength] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);

  useImperativeHandle(
    onRef,
    useMemoizedFn(() => {
      return {
        submit: submit,
      };
    })
  );

  const defaultSvcConfig = useMemoizedFn(() => {
    if (showType === SvcShowTypeEnum.JOB_OPTIONS) {
      const res =
        sku?.jobOptions &&
        renderSvcGroup(sku?.jobOptions, SvcShowTypeEnum.JOB_OPTIONS);
      form.setFieldsValue({
        skipRequiredSvc: Boolean(sku?.skipRequiredSvc),
        skipJobOption: Boolean(sku?.skipJobOption),
        svcList: res,
      });
      setSvcListLength(res?.length || 0);
    } else if (showType === SvcShowTypeEnum.REQUIRED) {
      const res =
        sku?.requiredSvcGroup &&
        renderSvcGroup(sku?.requiredSvcGroup, SvcShowTypeEnum.REQUIRED);
      form.setFieldsValue({
        skipRequiredSvc: Boolean(sku?.skipRequiredSvc),
        skipJobOption: Boolean(sku?.skipJobOption),
        svcList: res,
      });
      setSvcListLength(res?.length || 0);
    } else if (showType === SvcShowTypeEnum.ADDITIONAL_JOB_OPTIONS) {
      const res =
        sku?.additionalJobOptions &&
        renderSvcGroup(
          sku?.additionalJobOptions,
          SvcShowTypeEnum.ADDITIONAL_JOB_OPTIONS
        );
      form.setFieldsValue({
        skipRequiredSvc: Boolean(sku?.skipRequiredSvc),
        skipJobOption: Boolean(sku?.skipJobOption),
        svcList: res,
      });
      setSvcListLength(res?.length || 0);
    } else if (showType === SvcShowTypeEnum.OPTIONAL) {
      const res =
        sku?.optionalSvcGroup &&
        renderSvcGroup(sku?.optionalSvcGroup, SvcShowTypeEnum.OPTIONAL);
      form.setFieldsValue({
        skipRequiredSvc: Boolean(sku?.skipRequiredSvc),
        skipJobOption: Boolean(sku?.skipJobOption),
        svcList: res,
      });
      setSvcListLength(res?.length || 0);
    }
  });

  useEffect(() => {
    defaultSvcConfig();
  }, [sku, defaultSvcConfig]);

  const renderSvcGroup = (svcGroup: SvcGroup, svcType: SvcShowTypeEnum) => {
    const svcIdGroup = svcGroup.svcIdGroup;
    const svcSpecIdGroup = svcGroup.svcSpecIdGroup;
    const svcTypeIdGroup = svcGroup.svcTypeIdGroup;
    const svcIdGroupList = (svcIdGroup?.idGroups || [])
      ?.map((item) => {
        return {
          svcType: svcType,
          ids: item.ids.map((item) => `svc${item}`),
          svcGenre: item.exclusiveType,
          svcGroupLevel: SKU_SERVICE_TYPE_ENUM.SERVICE,
        };
      })
      ?.flat();
    const svcSpecIdGroupList = (svcSpecIdGroup?.idGroups || [])
      ?.map((item) => {
        return {
          svcType: svcType,
          ids: item.ids.map((item) => `spec${item}`),
          svcGenre: item.exclusiveType,
          svcGroupLevel: SKU_SERVICE_TYPE_ENUM.SERVICE_SPEC,
        };
      })
      ?.flat();
    const svcTypeIdGroupList = (svcTypeIdGroup?.idGroups || [])
      ?.map((item) => {
        return {
          svcType: svcType,
          ids: item.ids.map((item) => `type${item}`),
          svcGenre: item.exclusiveType,
          svcGroupLevel: SKU_SERVICE_TYPE_ENUM.SERVICE_TYPE,
        };
      })
      ?.flat();
    let returnData = [];

    if (svcIdGroup?.ids?.length) {
      returnData.push({
        svcType: svcType,
        ids: svcIdGroup.ids.map((item) => `svc${item}`),
        svcGenre: svcIdGroup.exclusiveType,
        svcGroupLevel: SKU_SERVICE_TYPE_ENUM.SERVICE,
      });
    }
    if (svcIdGroupList?.length) {
      returnData = returnData.concat(svcIdGroupList);
    }
    if (svcSpecIdGroup?.ids?.length) {
      returnData.push({
        svcType: svcType,
        ids: svcSpecIdGroup.ids.map((item) => `spec${item}`),
        svcGenre: svcSpecIdGroup.exclusiveType,
        svcGroupLevel: SKU_SERVICE_TYPE_ENUM.SERVICE_SPEC,
      });
    }

    if (svcSpecIdGroupList?.length) {
      returnData = returnData.concat(svcSpecIdGroupList);
    }

    if (svcTypeIdGroup?.ids?.length) {
      returnData.push({
        svcType: svcType,
        ids: svcTypeIdGroup.ids.map((item) => `type${item}`),
        svcGenre: svcTypeIdGroup.exclusiveType,
        svcGroupLevel: SKU_SERVICE_TYPE_ENUM.SERVICE_TYPE,
      });
    }

    if (svcTypeIdGroupList?.length) {
      returnData = returnData.concat(svcTypeIdGroupList);
    }

    return returnData;
  };

  const svcListHandle = (svcList: SvcTreeValue[]) => {
    const svcIdGroup = (svcList || []).filter(
      (item) => item.svcGroupLevel === SKU_SERVICE_TYPE_ENUM.SERVICE
    );
    const svcSpecIdGroup = (svcList || []).filter(
      (item) => item.svcGroupLevel === SKU_SERVICE_TYPE_ENUM.SERVICE_SPEC
    );
    const svcTypeIdGroup = (svcList || []).filter(
      (item) => item.svcGroupLevel === SKU_SERVICE_TYPE_ENUM.SERVICE_TYPE
    );
    return {
      svcIdGroup: {
        exclusiveType: SKU_SERVICE_GENRE_ENUM.ANY_OF,
        idGroups: svcIdGroup
          .filter((item) => item.svcGenre === SKU_SERVICE_GENRE_ENUM.ONE_OF)
          .map((item) => {
            return {
              ids: Array.from(
                new Set((item.ids || []).map((id) => +id.replace('svc', '')))
              ),
              exclusiveType: SKU_SERVICE_GENRE_ENUM.ONE_OF,
            };
          }),
        ids: Array.from(
          new Set(
            svcIdGroup
              .filter((item) => item.svcGenre === SKU_SERVICE_GENRE_ENUM.ANY_OF)
              .map((item) =>
                (item.ids || []).map((id) => +id.replace('svc', ''))
              )
              .flat()
          )
        ),
      },
      svcSpecIdGroup: {
        exclusiveType: SKU_SERVICE_GENRE_ENUM.ANY_OF,
        idGroups: svcSpecIdGroup
          .filter((item) => item.svcGenre === SKU_SERVICE_GENRE_ENUM.ONE_OF)
          .map((item) => {
            return {
              ids: Array.from(
                new Set((item.ids || []).map((id) => +id.replace('spec', '')))
              ),
              exclusiveType: SKU_SERVICE_GENRE_ENUM.ONE_OF,
            };
          }),
        ids: Array.from(
          new Set(
            svcSpecIdGroup
              .filter((item) => item.svcGenre === SKU_SERVICE_GENRE_ENUM.ANY_OF)
              .map((item) =>
                (item.ids || []).map((id) => +id.replace('spec', ''))
              )
              .flat()
          )
        ),
      },
      svcTypeIdGroup: {
        exclusiveType: SKU_SERVICE_GENRE_ENUM.ANY_OF,
        idGroups: svcTypeIdGroup
          .filter((item) => item.svcGenre === SKU_SERVICE_GENRE_ENUM.ONE_OF)
          .map((item) => {
            return {
              ids: Array.from(
                new Set((item.ids || []).map((id) => +id.replace('type', '')))
              ),
              exclusiveType: SKU_SERVICE_GENRE_ENUM.ONE_OF,
            };
          }),
        ids: Array.from(
          new Set(
            svcTypeIdGroup
              .filter((item) => item.svcGenre === SKU_SERVICE_GENRE_ENUM.ANY_OF)
              .map((item) =>
                (item.ids || []).map((id) => +id.replace('type', ''))
              )
              .flat()
          )
        ),
      },
    };
  };

  const submit = useMemoizedFn(async () => {
    try {
      setLoading(true);
      let refData = {};
      if (typeof refSubmitData === 'function') {
        refData = await refSubmitData();
      }
      const value = await form.validateFields();
      // if (
      //   !!value.svcList?.filter(
      //     (item: SvcTreeValue) => !item?.svcGroupLevel || !item?.svcGenre
      //   ).length
      // ) {
      //   throw new Error('error');
      // }
      const svcList = Boolean(svcListLength) ? cloneDeep(value.svcList) : [];
      const jobOptionsSvc = svcList?.filter(
        (item: SvcTreeValue) => item?.svcType === SvcShowTypeEnum.JOB_OPTIONS
      );
      const requiredSvcGroup = svcList?.filter(
        (item: SvcTreeValue) => item?.svcType === SvcShowTypeEnum.REQUIRED
      );
      const additionalJobOptions = svcList?.filter(
        (item: SvcTreeValue) =>
          item?.svcType === SvcShowTypeEnum.ADDITIONAL_JOB_OPTIONS
      );
      const optionalSvcGroup = svcList?.filter(
        (item: SvcTreeValue) => item?.svcType === SvcShowTypeEnum.OPTIONAL
      );
      if (showType === SvcShowTypeEnum.JOB_OPTIONS) {
        await dispatch.sku.updateSvc({
          ...refData,
          jobOptions: svcListHandle(jobOptionsSvc),
          // showType: SvcShowTypeEnum.JOB_OPTIONS,
          version: sku.version,
          id: sku.id,
        });
      } else if (showType === SvcShowTypeEnum.ADDITIONAL_JOB_OPTIONS) {
        await dispatch.sku.updateSvc({
          ...refData,
          additionalJobOptions: svcListHandle(additionalJobOptions),
          // showType: SvcShowTypeEnum.ADDITIONAL_JOB_OPTIONS,
          version: sku.version,
          id: sku.id,
        });
      } else if (showType === SvcShowTypeEnum.REQUIRED) {
        await dispatch.sku.updateSvc({
          ...refData,
          requiredSvcGroup: svcListHandle(requiredSvcGroup),
          // showType: SvcShowTypeEnum.REQUIRED,
          version: sku.version,
          id: sku.id,
        });
      } else if (showType === SvcShowTypeEnum.OPTIONAL) {
        await dispatch.sku.updateSvc({
          ...refData,
          optionalSvcGroup: svcListHandle(optionalSvcGroup),
          // showType: SvcShowTypeEnum.OPTIONAL,
          version: sku.version,
          id: sku.id,
        });
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  });

  const onValueChangeHandle = (
    value: any,
    allValue: { svcList: string | any[] }
  ) => {
    setSvcListLength(allValue.svcList?.length || 0);
  };

  useUpdateEffect(() => {
    if (!svcListLength) {
      submit();
    }
  }, [svcListLength, submit]);

  return (
    <Form
      form={form}
      onValuesChange={onValueChangeHandle}
      className="jobOption-config"
    >
      <Form.Item hidden={!svcListLength || svcListLength < 3}>
        <RevxButton
          brandGreen
          disabled={disabled}
          loading={loading}
          onClick={() => {
            submit();
          }}
        >
          Apply
        </RevxButton>
      </Form.Item>
      <Form.List name="svcList">
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field, index) => (
              <Form.Item {...field}>
                <SvcTree
                  sku={sku}
                  fields={fields}
                  index={index}
                  remove={remove}
                  showType={showType}
                  disabled={disabled}
                  option={[
                    SvcShowTypeEnum.ADDITIONAL_JOB_OPTIONS,
                    SvcShowTypeEnum.OPTIONAL,
                  ].includes(showType)}
                  required
                />
              </Form.Item>
            ))}
            <RevxFlexLayout justify="space-between">
              <div hidden={disabled}>
                <UnderlineText
                  color="blue"
                  onClick={() => {
                    if (!disabled) {
                      add();
                    }
                  }}
                >
                  <span>Add Group</span>
                </UnderlineText>
              </div>
              <RevxButton
                hidden={!svcListLength}
                brandGreen
                disabled={disabled}
                loading={loading}
                onClick={() => {
                  submit();
                }}
              >
                Apply
              </RevxButton>
            </RevxFlexLayout>
          </>
        )}
      </Form.List>
    </Form>
  );
};
